import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HashMap, TranslocoModule } from '@jsverse/transloco';
import { SpinnerComponent } from '@mzic/mzic-components';
import {
  AuthInteraction,
  AuthInteractionEndedReason,
  AuthInteractionLastError,
} from '../../../../interfaces/collection.interface';

export interface AuthInteractionRendererState {
  payload: Record<string, unknown>;
  submitDisabled: boolean;
}

export type AuthInteractionFooterAction = 'SUBMIT' | 'RESTART';

export interface TwoFaCodeRendererState extends AuthInteractionRendererState {
  /** `null` while the digit boxes submit on their own. */
  footer: AuthInteractionFooterAction | null;
  /** A code was sent and its result has not arrived yet. */
  verifying: boolean;
}

interface TwoFaStatus {
  tone: 'neutral' | 'error' | 'warning';
  spinner: boolean;
  key: string;
  params: HashMap;
  /** Stable sentence announced instead of the (ticking) visible text. */
  announceKey: string | null;
}

/** Beyond this many characters the boxes no longer fit the 480px flow. */
const MAX_DIGIT_BOXES = 8;
const EMPHASIS_MARK = '⁣';
const CODE_KEY_PREFIX = 'lang.auth_interaction_two_fa_';
const PUSH_KEY_PREFIX = 'lang.auth_interaction_push_';

@Component({
  selector: 'lib-two-fa-code-interaction',
  imports: [CommonModule, ReactiveFormsModule, TranslocoModule, SpinnerComponent],
  templateUrl: './two-fa-code-interaction.component.html',
  styleUrl: './two-fa-code-interaction.component.scss',
})
export class TwoFaCodeInteractionComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) authInteraction!: AuthInteraction;
  /** The verification already ended: no code entry, only the way back. */
  @Input() endedReason: AuthInteractionEndedReason | null = null;
  @Input() providerName = '';
  @Input() loading = false;
  @Input() resending = false;
  /**
   * Set when a request failed. Only its presence matters: the flow already
   * showed its standard toast, and backend text is never shown here.
   */
  @Input() submitError: string | null = null;

  @Output() rendererStateChange = new EventEmitter<TwoFaCodeRendererState>();
  @Output() submitRequested = new EventEmitter<void>();
  @Output() resendRequested = new EventEmitter<void>();

  @ViewChildren('digitInput')
  private readonly digitInputs?: QueryList<ElementRef<HTMLInputElement>>;

  readonly codeControl = new FormControl('', { nonNullable: true });
  readonly digits = signal<string[]>([]);
  /** The user changed the code since the last answer was rejected. */
  readonly codeEdited = signal(false);
  private readonly now = signal(Date.now());

  private lockedUntil: number | null = null;
  private resendAvailableAt: number | null = null;
  /** End of a push request (shown as a countdown note). */
  private expiresAt: number | null = null;
  private clock: ReturnType<typeof setInterval> | null = null;
  /** A code was sent; the entry stays held until a new prompt or an error. */
  private awaitingResult = false;
  private submittedCode: string | null = null;
  /** The provider rejected this exact value; it cannot be sent again. */
  private rejectedCode: string | null = null;
  /** A request failed: the same full code may be sent again from the footer. */
  private retryOffered = false;

  constructor() {
    this.codeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitRendererState());
    this.destroyRef.onDestroy(() => this.stopClock());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading']?.currentValue) {
      this.submittedCode = this.code;
      this.awaitingResult = true;
      this.retryOffered = false;
    }
    const interactionChange = changes['authInteraction'];
    if (interactionChange) {
      this.applyInteraction(interactionChange.firstChange);
    }
    if (changes['submitError']?.currentValue) {
      // The request failed: the same code may be sent again as is.
      this.awaitingResult = false;
      this.retryOffered = true;
    }
    this.syncFieldDisabled();
    this.emitRendererState();
  }

  get isPush(): boolean {
    return this.authInteraction.kind === 'PUSH_APPROVAL';
  }

  /**
   * Fixed-length numeric codes get one box per digit and submit on their
   * own; other codes use one field; a push prompt has nothing to type.
   */
  get inputMode(): 'DIGITS' | 'FIELD' | 'NONE' {
    if (this.isPush) return 'NONE';
    const format = this.authInteraction.code_format;
    return format?.charset === 'NUMERIC' &&
      format.length !== null &&
      format.length <= MAX_DIGIT_BOXES
      ? 'DIGITS'
      : 'FIELD';
  }

  /** A push has its own icon; codes are drawn by channel. */
  get icon(): 'PUSH' | AuthInteraction['channel'] {
    return this.isPush ? 'PUSH' : this.authInteraction.channel;
  }

  /** Copy is keyed by kind; the code screen keeps its original keys. */
  get keyPrefix(): string {
    return this.isPush ? PUSH_KEY_PREFIX : CODE_KEY_PREFIX;
  }

  get expirySecondsLeft(): number {
    return secondsUntil(this.expiresAt, this.now());
  }

  get verifying(): boolean {
    return this.loading || this.awaitingResult;
  }

  get lockSecondsLeft(): number {
    return secondsUntil(this.lockedUntil, this.now());
  }

  get resendSecondsLeft(): number {
    return secondsUntil(this.resendAvailableAt, this.now());
  }

  get inputDisabled(): boolean {
    return this.verifying || this.resending || this.lockSecondsLeft > 0;
  }

  get showsCodeError(): boolean {
    return (
      this.authInteraction.last_error?.code === 'INVALID_CODE' &&
      !this.codeEdited() &&
      !this.verifying
    );
  }

  /** The single line under the code; the first matching state wins. */
  get status(): TwoFaStatus | null {
    const prefix = this.keyPrefix;
    if (this.endedReason) {
      return statusOf(
        'warning',
        `${prefix}ended_${this.endedReason.toLowerCase()}`,
      );
    }
    if (this.isPush) {
      return statusOf('neutral', `${prefix}waiting`, {}, true);
    }
    if (this.lockSecondsLeft > 0) {
      return {
        ...statusOf('warning', `${prefix}locked`, {
          time: formatClock(this.lockSecondsLeft),
        }),
        announceKey: `${prefix}too_many_attempts`,
      };
    }
    if (this.verifying) {
      // In field mode the footer button already reads VERIFICANDO.
      return this.inputMode === 'DIGITS'
        ? statusOf(
            'neutral',
            `${prefix}verifying`,
            { provider: this.providerName },
            true,
          )
        : null;
    }
    if (
      this.authInteraction.last_error?.code === 'TOO_MANY_ATTEMPTS' &&
      this.lockedUntil === null &&
      !this.codeEdited()
    ) {
      // Throttled without a known wait: warn, keep the prompt usable.
      return statusOf('warning', `${prefix}too_many_attempts`);
    }
    return this.showsCodeError
      ? statusOf('error', `${prefix}invalid_code`)
      : null;
  }

  get descriptionKey(): string {
    const prefix = this.keyPrefix;
    const { channel, destination_hint, authenticator_label } =
      this.authInteraction;
    if (this.isPush) {
      return authenticator_label
        ? `${prefix}description_label`
        : `${prefix}description`;
    }
    switch (channel) {
      case 'EMAIL':
        if (!destination_hint) return `${prefix}description_email`;
        return this.inputMode === 'DIGITS'
          ? `${prefix}description_email_destination_digits`
          : `${prefix}description_email_destination`;
      case 'SMS':
        return destination_hint
          ? `${prefix}description_sms_destination`
          : `${prefix}description_sms`;
      case 'AUTHENTICATOR_APP':
        return authenticator_label
          ? `${prefix}description_authenticator_app_label`
          : `${prefix}description_authenticator_app`;
      case 'UNKNOWN':
        return `${prefix}description_unknown`;
    }
  }

  get descriptionParams(): HashMap {
    return {
      provider: this.providerName,
      length: this.authInteraction.code_format?.length ?? '',
      destination: EMPHASIS_MARK,
      authenticator: EMPHASIS_MARK,
    };
  }

  /** Splits the translated copy around the emphasized destination or app. */
  descriptionParts(text: string): [string, string, string] {
    const [lead, ...rest] = text.split(EMPHASIS_MARK);
    if (rest.length === 0) return [text, '', ''];
    const emphasis =
      this.authInteraction.channel === 'AUTHENTICATOR_APP'
        ? this.authInteraction.authenticator_label
        : this.authInteraction.destination_hint;
    return [lead, emphasis ?? '', rest.join('')];
  }

  /** A code is resent; a push request is sent again. */
  get resendKeys(): { help: string; action: string; countdown: string } {
    return this.isPush
      ? {
          help: `${PUSH_KEY_PREFIX}resend_help`,
          action: `${PUSH_KEY_PREFIX}resend`,
          countdown: `${PUSH_KEY_PREFIX}resend_countdown`,
        }
      : {
          help: 'lang.auth_interaction_resend_help',
          action: 'lang.auth_interaction_resend',
          countdown: `${CODE_KEY_PREFIX}resend_countdown`,
        };
  }

  /** Without a resend link, a code prompt explains its channel instead. */
  get noResendHintKey(): string {
    return `${CODE_KEY_PREFIX}no_resend_${this.authInteraction.channel.toLowerCase()}`;
  }

  get fieldLabelKey(): string {
    return this.authInteraction.channel === 'AUTHENTICATOR_APP'
      ? 'lang.auth_interaction_code_label_app'
      : 'lang.auth_interaction_code_label';
  }

  get fieldPlaceholderKey(): string {
    return this.authInteraction.channel === 'AUTHENTICATOR_APP'
      ? 'lang.auth_interaction_code_placeholder_app'
      : 'lang.auth_interaction_code_placeholder';
  }

  get fieldInputMode(): 'numeric' | 'text' {
    return this.authInteraction.code_format?.charset === 'NUMERIC'
      ? 'numeric'
      : 'text';
  }

  formatClock(seconds: number): string {
    return formatClock(seconds);
  }

  onFieldInput(): void {
    this.codeEdited.set(true);
  }

  onFieldEnter(): void {
    if (!this.submitDisabled) {
      this.submitRequested.emit();
    }
  }

  onDigitFocus(event: FocusEvent): void {
    // Typing over a filled box replaces its digit.
    (event.target as HTMLInputElement).select();
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const typed = input.value.replace(/\D/g, '');
    this.codeEdited.set(true);
    if (typed.length > 1) {
      // One-time-code autofill drops the whole code into one box.
      this.fillDigits(index, typed);
      input.value = this.digits()[index];
      return;
    }
    const next = [...this.digits()];
    next[index] = typed;
    this.digits.set(next);
    input.value = typed;
    if (typed && index < next.length - 1) this.focusDigit(index + 1);
    this.onDigitsChanged();
  }

  onDigitKeyDown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.digits()[index] && index > 0) {
      event.preventDefault();
      const next = [...this.digits()];
      next[index - 1] = '';
      this.digits.set(next);
      this.focusDigit(index - 1);
      this.onDigitsChanged();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.focusDigit(index - 1);
    } else if (event.key === 'ArrowRight' && index < this.digits().length - 1) {
      this.focusDigit(index + 1);
    }
  }

  onDigitPaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    if (this.inputDisabled) return;
    const pasted = (event.clipboardData?.getData('text') ?? '').replace(
      /\D/g,
      '',
    );
    if (!pasted) return;
    this.codeEdited.set(true);
    this.fillDigits(index, pasted);
  }

  onResend(): void {
    if (
      this.verifying ||
      this.resending ||
      !this.authInteraction.resend_supported ||
      this.resendSecondsLeft > 0
    ) {
      return;
    }
    this.resendRequested.emit();
  }

  private get code(): string {
    return this.inputMode === 'DIGITS'
      ? this.digits().join('')
      : this.codeControl.value.trim();
  }

  private get submitDisabled(): boolean {
    if (this.endedReason || this.isPush || this.inputDisabled) return true;
    if (this.inputMode === 'DIGITS') {
      return this.digits().some((digit) => !digit);
    }
    const code = this.code;
    return !code || this.codeControl.invalid || code === this.rejectedCode;
  }

  /** A full code always fills from the first box; a partial one from `index`. */
  private fillDigits(index: number, chars: string): void {
    const next = [...this.digits()];
    const start = chars.length >= next.length ? 0 : index;
    for (let offset = 0; offset < chars.length; offset++) {
      if (start + offset >= next.length) break;
      next[start + offset] = chars[offset];
    }
    this.digits.set(next);
    this.focusDigit(Math.min(start + chars.length, next.length - 1));
    this.onDigitsChanged();
  }

  private onDigitsChanged(): void {
    this.emitRendererState();
    if (!this.submitDisabled) {
      this.submitRequested.emit();
    }
  }

  /**
   * Timers always follow the latest prompt. The code entry only resets when
   * the prompt answers a sent code (or first arrives); a reload after a
   * resend keeps what the user typed.
   */
  private applyInteraction(firstChange: boolean): void {
    const interaction = this.authInteraction;
    const now = Date.now();
    this.lockedUntil = lockDeadline(interaction.last_error, now);
    this.resendAvailableAt = parseInstant(interaction.resend_available_at);
    this.expiresAt = this.isPush ? parseInstant(interaction.expires_at) : null;
    this.now.set(now);
    this.syncClock();
    this.codeControl.setValidators(this.fieldValidators());

    const answered = firstChange || this.awaitingResult;
    const rejected = interaction.last_error?.code === 'INVALID_CODE';
    this.awaitingResult = false;
    if (!rejected) this.rejectedCode = null;

    const boxes =
      this.inputMode === 'DIGITS' ? (interaction.code_format?.length ?? 0) : 0;
    if (answered || this.digits().length !== boxes) {
      this.digits.set(Array.from({ length: boxes }, () => ''));
    }
    if (answered) {
      this.codeEdited.set(false);
      this.retryOffered = false;
      if (rejected && !firstChange) {
        // A rejected code clears the boxes but stays in the free field (D1).
        this.rejectedCode = this.submittedCode;
      } else {
        this.codeControl.setValue('', { emitEvent: false });
      }
      if (rejected && this.inputMode === 'DIGITS') {
        setTimeout(() => this.focusDigit(0));
      }
    }
    this.codeControl.updateValueAndValidity({ emitEvent: false });
  }

  private fieldValidators(): ValidatorFn[] {
    const validators = [Validators.required];
    const schema = this.codeSchema;
    if (typeof schema?.minLength === 'number') {
      validators.push(Validators.minLength(schema.minLength));
    }
    if (typeof schema?.maxLength === 'number') {
      validators.push(Validators.maxLength(schema.maxLength));
    }
    if (typeof schema?.pattern === 'string') {
      validators.push(Validators.pattern(schema.pattern));
    }
    return validators;
  }

  private syncFieldDisabled(): void {
    if (this.inputDisabled) {
      this.codeControl.disable({ emitEvent: false });
    } else {
      this.codeControl.enable({ emitEvent: false });
    }
  }

  private emitRendererState(): void {
    this.rendererStateChange.emit({
      payload: { code: this.code },
      submitDisabled: this.submitDisabled,
      footer: this.footer,
      verifying: this.verifying,
    });
  }

  private get footer(): AuthInteractionFooterAction | null {
    if (this.endedReason) return 'RESTART';
    if (this.inputMode === 'FIELD') return 'SUBMIT';
    // A push is checked by the flow. Digit boxes submit on their own; after
    // a failed request the same code can be sent again from the footer.
    return this.inputMode === 'DIGITS' && this.retryOffered && !this.verifying
      ? 'SUBMIT'
      : null;
  }

  private syncClock(): void {
    const now = Date.now();
    const pending = [this.lockedUntil, this.resendAvailableAt, this.expiresAt]
      .some((deadline) => (deadline ?? 0) > now);
    if (pending && !this.clock) {
      this.clock = setInterval(() => this.onClockTick(), 1000);
    } else if (!pending) {
      this.stopClock();
    }
  }

  private onClockTick(): void {
    const wasLocked = this.lockSecondsLeft > 0;
    this.now.set(Date.now());
    if (wasLocked && this.lockSecondsLeft === 0) {
      this.syncFieldDisabled();
      this.emitRendererState();
    }
    this.syncClock();
  }

  private stopClock(): void {
    if (this.clock) {
      clearInterval(this.clock);
      this.clock = null;
    }
  }

  private focusDigit(index: number): void {
    this.digitInputs?.get(index)?.nativeElement.focus();
  }

  private get codeSchema(): {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
  } | null {
    const properties = this.authInteraction.payload_schema?.['properties'];
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      return null;
    }
    const code = (properties as Record<string, unknown>)['code'];
    if (!code || typeof code !== 'object' || Array.isArray(code)) {
      return null;
    }
    return code as { minLength?: number; maxLength?: number; pattern?: string };
  }
}

function statusOf(
  tone: TwoFaStatus['tone'],
  key: string,
  params: HashMap = {},
  spinner = false,
): TwoFaStatus {
  return { tone, spinner, key, params, announceKey: null };
}

/** Throttling locks until an absolute instant when the backend gives one. */
function lockDeadline(
  error: AuthInteractionLastError | null,
  now: number,
): number | null {
  if (error?.code !== 'TOO_MANY_ATTEMPTS') return null;
  return (
    parseInstant(error.retry_available_at) ??
    (error.retry_after_seconds ? now + error.retry_after_seconds * 1000 : null)
  );
}

function parseInstant(value: string | null): number | null {
  const instant = value ? Date.parse(value) : NaN;
  return Number.isNaN(instant) ? null : instant;
}

function secondsUntil(deadline: number | null, now: number): number {
  return deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
}

function formatClock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
